import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Modal, Steps } from 'antd';
import useForm from '../../shared/useFormHook';
import Input from '../../shared/input/Input';
import moment from 'moment';
import { deleteProjectInfo, saveProjectInfo, updateProjectInfo } from '../../../../services/Dashboard';
import SkillsSelector from './SkillsSelector';
import TuringHelpBtn from '../../shared/TuringHelpBtn/TuringHelpBtn';

const { Step } = Steps;
function SideProjectsForm ({ service, customClass }) {
  const { addSugg, onPanelSelect, onSaveClick, removeEvent, save, setCompleted } = service;
  const { SideProjects } = service.state;
  const { isReadOnly } = service.props;
  const [addNew, setAddNew] = useState(false);
  const [newItem, setNewItem] = useState(null);

  const onSave = useCallback(async (isAutoSave) => {
    let listItemsToSave = [...SideProjects];
    if (newItem && addNew && !isAutoSave) listItemsToSave.push(newItem);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));
    let successResult = true;
    const success = (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'SideProjects') successResult = 'SideProjects';
      return success;
    });
    if (!isAutoSave) {
      if (success) {
        setAddNew(false);
        setNewItem(null);
      } else {
        setCompleted('SideProjects', false);
        onPanelSelect('SideProjects');
      }
    }
    return success ? successResult : false;
  }, [newItem, addNew, SideProjects]);

  useEffect(() => {
    addSugg('SideProjects', { id: 'SideProjects1', content: <p>Adding side projects let you showcase additional skills that might be attractive to clients.</p> });
    addSugg('SideProjects', { id: 'SideProjects2', content: <p>Keep in mind that the tech stacks you add in your side projects don&apos;t count as work experience.</p> });
    onSaveClick('SideProjects', onSave);
    return () => removeEvent('SideProjects');
  }, [onSave]);

  const addNewItem = () => {
    if (!newItem) {
      const item = {};
      setNewItem(item);
    }
    setAddNew(true);
  };

  const handleCancelAddingNewItem = () => {
    setNewItem(null);
    setAddNew(false);
  };

  return (
    <div className={customClass || ''}>
      <div className="TuringStepsFormv2">
        <p>Showcase the projects to demonstrate other desirable skills. These projects can help companies understand your passion for engineering, initiative, skill, and a love for continuous improvement. This can include your projects in college, internships, courses, hobbies, et al. The skills/tech stacks you add in these projects do not add to the total work experience.</p>
        <Steps progressDot current={SideProjects.length - 1} direction="vertical">
          {SideProjects.map(item => <SideProjectsItem key={item.id} service={service} item={item} />)}
          {addNew ? (
            <SideProjectsItem service={service} item={newItem} cancelAddingNewItem={handleCancelAddingNewItem} />
          ) : (isReadOnly ? null : (
            <Step description={<span className="additembtn" onClick={addNewItem}><span className="material-icons" aria-hidden="true">add</span> Add side project</span>} />
          ))}
        </Steps>
      </div>
      {isReadOnly ? null : (
        <div className="FinalizeResumeV2FormBtns">
          <Button onClick={() => { setAddNew(false); onPanelSelect(null); }}>Cancel</Button>
          <Button type="primary" onClick={() => save('SideProjects')}>Update information</Button>
        </div>
      )}
    </div>
  );
}

SideProjectsForm.propTypes = {
  service: PropTypes.any,
  customClass: PropTypes.string
};

const years = [];
for (let i = 0; i <= 35; i++) {
  years.push({
    value: new Date().getFullYear() - i,
    label: new Date().getFullYear() - i
  });
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
].map((month) => ({ value: month, label: month }));

const SideProjectsItem = ({ item, service, ...props }) => {
  const [open, setOpen] = useState(false);
  const { loadData } = service;
  const { baseSkills, skillInfo } = service.state;
  const { isReadOnly } = service.props;
  const { data, bind, setData, getData, setValue, setError, getErrors, resetError, getChanges } = useForm({}, service.saveDebounce);

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    if (!item) return;
    item.project_skills = Array.isArray(item.project_skills) ? item.project_skills : (item.project_skills || '')
      .split(',')
      .map(value => parseInt(value))
      .filter(value => value);

    setData(item);
    resetError();

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = ['project_name', 'start_month', 'start_year', 'project_skills', 'details'];
      const changedFields = await getChanges(item, [...fields, 'end_month', 'end_year', 'url']);
      if (!changedFields.length) return true;

      const data = await getData();
      if (data.end_year !== 0) {
        fields.push('end_month');
        fields.push('end_year');
      }

      if (isAutoSave) {
        if (!data.id || fields.find(field => !data[field])) {
          return false;
        }
      } else {
        fields.forEach(field => {
          if (!data[field]) {
            setError(field);
          }
        });
      }

      if (data.start_month && data.start_year && data.end_year && data.end_month) {
        const getDate = (start_end) => {
          const month = data[`${start_end}_month`];
          const year = data[`${start_end}_year`];
          if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
          return null;
        };
        const start_date = getDate('start');
        const end_date = getDate('end');
        if (moment(start_date.format('YYYY-MM-01')).isAfter(end_date.format('YYYY-MM-01'))) {
          if (isAutoSave) return false;
          setError('start_month');
          setError('start_year');
          setError('end_month');
          setError('end_year');
        }
      }

      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        const promiseObj = data.id ? updateProjectInfo({ ...data, cId: data.id }) : saveProjectInfo(data);
        return promiseObj
          .then(() => {
            if (isAutoSave) {
              service.setState((s) => {
                return { ...s, SideProjects: s.SideProjects.map(elm => elm.id === data.id ? { ...elm, ...data } : elm) };
              });
            }
            return 'SideProjects';
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: <h1 className="Delete-work-experience">Delete the project?</h1>,
        content: 'This action cannot be undone.',
        okText: 'Yes, delete',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          return deleteProjectInfo({ cId: item.id })
            .then(result => {
              loadData(['SideProjects']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  let title = (
    <div className="SideProjectsItem-title" onClick={() => setOpen(!open)}>
      <span className="SideProjectsItem-project_name">{item.project_name}</span>
      {open && !isReadOnly ? <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span> : null}
      <span className="material-icons" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
    </div>
  );

  if (!item.id) {
    title = (
      <div className="SideProjectsItem-title">
        Add side project
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span>
      </div>
    );
  }

  let form = null;
  if (open) {
    form = (
      <div className="SideProjectsItem-form">
        <div className="a"><Input label="Project Name" {...bind('project_name')} /></div>
        <div className="b"><Input label="URL or GitHub Repository" {...bind('url')} /></div>
        <div className="c SideProjectsItem-date"><Input.Select options={months} label="Starting date" {...bind('start_month')} /></div>
        <div className="d SideProjectsItem-date"><Input.Select options={years} label="" {...bind('start_year')} /></div>
        <div className="e SideProjectsItem-date">{data.end_year !== 0 ? <Input.Select options={months} label="Ending date" {...bind('end_month')} /> : null}</div>
        <div className="f SideProjectsItem-date">{data.end_year !== 0 ? <Input.Select options={years} label="" {...bind('end_year')} /> : null}</div>
        <div className="g">
          <Checkbox
            checked={data.end_year === 0}
            onChange={(e) => setValue('end_year', e.target.checked ? 0 : (item.end_year === 0 ? null : (item.end_year || null)))}
          >
            Still working here
          </Checkbox>
        </div>
        <div className="h">
          <Input.TextArea className="SideProjectsItem-Projectdescription" label={<TuringHelpBtn help="Best way is to describe your project starting with the problem statement, what you did to solve it, and the result of the project. Giving concrete examples of how you solved problems at previous jobs are compelling testimonials of your abilities.">Project description</TuringHelpBtn>} {...bind('details')} />
        </div>
        <div className="i">
          <SkillsSelector
            label={<TuringHelpBtn help="The skills utilized in the project will be automatically added here. You can add other related tech stacks manually.">Tech stacks</TuringHelpBtn>}
            {...bind('project_skills')}
            allSkills={baseSkills}
            passed_skills={skillInfo.passed_skills}
            skills_inprogress={skillInfo.skills_inprogress}
            userAllSkills={skillInfo.all_skills}
          />
        </div>
      </div>
    );
  }

  return (
    <Step title={title} description={form} {...props} />
  );
};

SideProjectsItem.propTypes = {
  item: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  service: PropTypes.any
};

export default SideProjectsForm;
